import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../api';
import { showModalSuccess } from '../utils/sweetAlert';
import BrandMark from '../components/BrandMark';

const CambiarContrasena = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const checks = useMemo(
    () => ({
      minLength: newPassword.length >= 6,
      match: newPassword.length > 0 && newPassword === confirmPassword,
    }),
    [newPassword, confirmPassword],
  );

  const canSubmit = checks.minLength && checks.match && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!checks.minLength) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (!checks.match) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await changePassword(newPassword.trim());
      await showModalSuccess(
        'Contraseña actualizada',
        'Ya podés acceder al consultorio con tu nueva clave.',
      );
      await refreshUser?.();
      navigate('/consultorio', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'No se pudo guardar la contraseña. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login', { replace: true });
    return null;
  }
  if (!user.tenant_id) {
    navigate('/', { replace: true });
    return null;
  }

  const consultorioLabel = user.tenant_name || 'tu consultorio';

  return (
    <div className="np-auth-page np-cp-page storefront-root">
      <div className="np-auth-shell np-cp-shell">
        <aside className="np-auth-aside np-cp-aside" aria-hidden="false">
          <div className="np-auth-aside-inner">
            <BrandMark to="/" className="np-auth-brand" markClassName="np-logo-mark" nameClassName="np-auth-brand-name" />
            <span className="np-auth-tag">
              <i className="bi bi-shield-lock" /> Acceso seguro
            </span>
            <h2>
              Configurá tu <em>clave personal</em>
            </h2>
            <p>
              La contraseña temporal que recibiste por correo solo sirve para el primer ingreso. Elegí una clave
              propia para proteger la información de {consultorioLabel}.
            </p>
            <ul className="np-auth-perks">
              <li>
                <i className="bi bi-check2-circle" />
                Obligatorio en el primer acceso
              </li>
              <li>
                <i className="bi bi-eye-slash" />
                No compartas tu contraseña con terceros
              </li>
              <li>
                <i className="bi bi-hospital" />
                Después continuarás al panel del consultorio
              </li>
            </ul>
          </div>
        </aside>

        <main className="np-auth-card np-cp-card">
          <BrandMark to="/" className="np-auth-brand np-auth-mobile-brand" markClassName="np-logo-mark" />

          <header className="np-auth-card-header np-cp-header">
            <div className="np-cp-icon" aria-hidden>
              <i className="bi bi-key-fill" />
            </div>
            <h1>Nueva contraseña</h1>
            <p>
              Por seguridad debés definir una contraseña personal antes de continuar
              {user.tenant_name ? (
                <>
                  {' '}
                  en <strong>{user.tenant_name}</strong>
                </>
              ) : null}
              .
            </p>
            {user.email ? (
              <p className="np-cp-account">
                <i className="bi bi-envelope" aria-hidden />
                <span>{user.email}</span>
              </p>
            ) : null}
          </header>

          {error ? (
            <div className="np-auth-info np-auth-error" role="alert">
              <i className="bi bi-exclamation-circle" />
              <span>{error}</span>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="np-cp-form">
            <div className="np-auth-field">
              <label className="np-auth-label" htmlFor="cp-new-password">
                Nueva contraseña
              </label>
              <div className="np-auth-input-wrap">
                <i className="bi bi-lock np-auth-input-icon" aria-hidden />
                <input
                  id="cp-new-password"
                  type={showNew ? 'text' : 'password'}
                  className="np-auth-input np-auth-input--suffix"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Escribí tu nueva clave"
                  minLength={6}
                  required
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  className="np-auth-toggle-pwd"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <i className={`bi ${showNew ? 'bi-eye-slash' : 'bi-eye'}`} />
                </button>
              </div>
            </div>

            <div className="np-auth-field">
              <label className="np-auth-label" htmlFor="cp-confirm-password">
                Confirmar contraseña
              </label>
              <div className="np-auth-input-wrap">
                <i className="bi bi-lock-fill np-auth-input-icon" aria-hidden />
                <input
                  id="cp-confirm-password"
                  type={showConfirm ? 'text' : 'password'}
                  className="np-auth-input np-auth-input--suffix"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Volvé a escribir la misma clave"
                  minLength={6}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="np-auth-toggle-pwd"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? 'Ocultar confirmación' : 'Mostrar confirmación'}
                >
                  <i className={`bi ${showConfirm ? 'bi-eye-slash' : 'bi-eye'}`} />
                </button>
              </div>
            </div>

            <ul className="np-cp-checklist" aria-live="polite">
              <li className={checks.minLength ? 'is-ok' : ''}>
                <i className={`bi ${checks.minLength ? 'bi-check-circle-fill' : 'bi-circle'}`} aria-hidden />
                Al menos 6 caracteres
              </li>
              <li className={checks.match ? 'is-ok' : ''}>
                <i className={`bi ${checks.match ? 'bi-check-circle-fill' : 'bi-circle'}`} aria-hidden />
                Ambas contraseñas coinciden
              </li>
            </ul>

            <button type="submit" className="np-auth-submit" disabled={!canSubmit}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  Guardando…
                </>
              ) : (
                <>
                  <i className="bi bi-shield-check" />
                  Guardar y continuar
                </>
              )}
            </button>
          </form>

          <p className="np-cp-footnote">
            <i className="bi bi-info-circle" aria-hidden />
            Si cerrás esta página sin guardar, se te volverá a pedir al intentar entrar al consultorio.
          </p>
        </main>
      </div>
    </div>
  );
};

export default CambiarContrasena;
